<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import GroupUrlBanner from '@/modules/workspace/components/group-url-banner.vue'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import MembershipAgreementInput from '@/modules/workspace/components/membership-agreement-input.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton, VButtonInvisible, VToggleButtonField } from '@/modules/shared/components'

const workspaceSettingsStore = useWorkspaceSettingsStore()
const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()

const selectedTab = computed(() => 'investor_settings' as const)

// Membership agreement state
const membershipAgreementFile = ref<File | null>(null)
const removeMembershipAgreement = ref(false)
const agreementInputRef = ref<InstanceType<typeof MembershipAgreementInput> | null>(null)

const handleAgreementUpdate = (payload: { file?: File; remove?: boolean }) => {
  if (payload.file) {
    membershipAgreementFile.value = payload.file
    removeMembershipAgreement.value = false
  } else if (payload.remove) {
    membershipAgreementFile.value = null
    removeMembershipAgreement.value = true
  } else {
    membershipAgreementFile.value = null
    removeMembershipAgreement.value = false
  }
}

const resetAgreementState = () => {
  membershipAgreementFile.value = null
  removeMembershipAgreement.value = false
  agreementInputRef.value?.reset()
}

const ACCREDITATION_LEVELS = [
  { value: 'non_accredited', label: 'Non-accredited' },
  { value: 'accredited', label: 'Accredited' },
  { value: 'qualified_purchaser', label: 'Qualified Purchaser' },
]

const isConnectionProfileComplete = computed(
  () => workspaceSettingsStore.workspace?.connection_profile_complete ?? false,
)

const form = ref({
  accreditation_country_code: '',
  accreditation_level_min: '',
  require_investor_profiles: true,
  public_investor_registration_enabled: false,
  show_investors_to_investors: false,
  show_chapters_to_investors: false,
  investor_aml_verification_enabled: false,
  connections_matching_enabled: false,
  connections_digest_enabled: true,
})

const setForm = () => {
  const settings = workspaceSettingsStore.settings
  const workspace = workspaceSettingsStore.workspace
  if (settings) {
    form.value = {
      accreditation_country_code: workspace?.accreditation_country_code ?? '',
      accreditation_level_min: workspace?.accreditation_level_min ?? '',
      require_investor_profiles: !(settings.skip_self_accreditation ?? false),
      public_investor_registration_enabled: settings.public_investor_registration_enabled ?? false,
      show_investors_to_investors: !(settings.hide_investors_from_investors ?? true),
      show_chapters_to_investors: settings.show_chapters_to_investors ?? false,
      investor_aml_verification_enabled: settings.investor_aml_verification_enabled ?? false,
      connections_matching_enabled: settings.connections_matching_enabled ?? false,
      connections_digest_enabled: settings.connections_digest_enabled ?? true,
    }
  }
}

watch(() => workspaceSettingsStore.settings, setForm, { immediate: true })

const submit = async () => {
  const {
    accreditation_country_code,
    accreditation_level_min,
    require_investor_profiles,
    show_investors_to_investors,
    ...rest
  } = form.value
  const settings = {
    ...rest,
    skip_self_accreditation: !require_investor_profiles,
    hide_investors_from_investors: !show_investors_to_investors,
  }
  await workspaceSettingsStore.updateSettings(settings, { silent: true })

  const workspacePayload: Record<string, unknown> = {
    accreditation_country_code,
    accreditation_level_min: accreditation_level_min || null,
  }
  if (membershipAgreementFile.value) {
    workspacePayload.membership_agreement = membershipAgreementFile.value
  } else if (removeMembershipAgreement.value) {
    workspacePayload.remove_membership_agreement = true
  }

  await workspaceSettingsStore.updateWorkspace(workspacePayload)
  resetAgreementState()
}

onMounted(() => {
  workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <form @submit.prevent="submit" class="space-y-6">
        <div class="text-lg font-semibold">Investor Settings</div>

        <fieldset class="rounded-lg border border-gray-300/50 p-4">
          <label for="accreditation_country" class="block text-sm font-medium text-gray-700">
            Accreditation requirement
          </label>
          <p class="mt-1 text-sm text-gray-500">
            Select the country whose accreditation standards will apply to your investors.
          </p>
          <select
            id="accreditation_country"
            v-model="form.accreditation_country_code"
            class="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option value="">Select a country</option>
            <option
              v-for="country in workspaceSettingsStore.options?.accreditation_countries"
              :key="country.value"
              :value="country.value"
            >
              {{ country.label }}
            </option>
          </select>

          <label for="accreditation_level_min" class="mt-4 block text-sm font-medium text-gray-700">
            Minimum accreditation level
          </label>
          <p class="mt-1 text-sm text-gray-500">
            Select the minimum level of accreditation required from your investors.
          </p>
          <select
            id="accreditation_level_min"
            v-model="form.accreditation_level_min"
            class="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
          >
            <option value="">Select a level</option>
            <option v-for="level in ACCREDITATION_LEVELS" :key="level.value" :value="level.value">
              {{ level.label }}
            </option>
          </select>
        </fieldset>

        <VToggleButtonField
          v-model="form.require_investor_profiles"
          label="Require investor profiles"
          description="When enabled, investors must complete their profile before accessing any investment information."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.public_investor_registration_enabled"
          label="Public investor registration"
          description="Allow investors to register through a public URL. You will still need to approve their access before they can join your group. When disabled, investors can only be invited by an admin."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <GroupUrlBanner
          v-if="form.public_investor_registration_enabled"
          :subdomain="workspaceSettingsStore.workspace?.subdomain ?? ''"
          description="Share this URL with investors so they can register for your group."
        />

        <VToggleButtonField
          v-model="form.show_investors_to_investors"
          label="Investor visibility"
          description="Allow investors to view the profiles of other investors in your group. They will only see profile picture, name, and email."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.show_chapters_to_investors"
          label="Sub account visibility"
          description="Allow investors to view sub accounts. Sub accounts will always be visible to group admins."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-if="authStore.is_site_admin"
          v-model="form.investor_aml_verification_enabled"
          label="AML screening"
          description="Enable continuous AML monitoring of all investors through our integration with sanctions.io."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <template v-if="workspaceStore.current.options.has_connections_access">
          <VToggleButtonField
            v-model="form.connections_matching_enabled"
            label="Connections matching"
            :description="
              isConnectionProfileComplete
                ? 'Enable automatic matching with investors based on your firm\'s candidate profile.'
                : 'Complete your firm\'s candidate profile before enabling connections matching.'
            "
            trueLabel="Enable"
            falseLabel="Disable"
            :disabled="!isConnectionProfileComplete"
          />

          <VToggleButtonField
            v-model="form.connections_digest_enabled"
            label="Weekly connections digest"
            description="Receive a weekly email summarizing new investor matches for your organization."
            trueLabel="Enable"
            falseLabel="Disable"
          />
        </template>

        <!-- Membership Agreement -->
        <fieldset class="rounded-lg border border-gray-300/50 p-4">
          <label class="block text-sm font-medium text-gray-700">Investor agreement</label>
          <p class="mt-1 text-sm text-gray-500">
            Upload a document that investors must acknowledge before accessing your organization, such as a membership
            agreement.
          </p>

          <div class="mt-3">
            <MembershipAgreementInput
              ref="agreementInputRef"
              :agreement="workspaceSettingsStore.membershipAgreement"
              @update="handleAgreementUpdate"
            />
          </div>
        </fieldset>

        <div class="pt-4">
          <VButton variant="v-blue" size="lg" type="submit" :loading="workspaceSettingsStore.loading">
            Save Changes
          </VButton>
        </div>
      </form>
    </WorkspaceSettingsLayout>
  </TheLayout>
</template>
